// frontend/src/App.jsx
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";

// Auth pages (public)
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

// Layout
import Layout from "./components/Layout";

// Admin pages
import Dashboard from "./pages/Dashboard";
import EntityList from "./pages/Entities/EntityList";
import EntityDetail from "./pages/Entities/EntityDetail";
import GroupManagement from "./pages/Groups/GroupManagement";
import VendorList from "./pages/Vendors/VendorList";
import VendorDetail from "./pages/Vendors/VendorDetail";
import PartnerList from "./pages/Partners/PartnerList";
import PartnerDetail from "./pages/Partners/PartnerDetail";
import PartnerNew from "./pages/Partners/PartnerNew";
import AgreementList from "./pages/Agreements/AgreementList";
import AgreementDetail from "./pages/Agreements/AgreementDetail";
import AgreementNew from "./pages/Agreements/AgreementNew";
import AddendumNew from "./pages/Agreements/AddendumNew";
import AdminSettings from "./pages/Settings/AdminSettings";
import TemplateList from "./pages/Templates/TemplateList";
import TemplateDetail from "./pages/Templates/TemplateDetail";
import PortalTemplates from "./pages/Templates/PortalTemplates";
import PermitList from "./pages/Permits/PermitList";

// Proposals
import ProposalList from "./pages/Proposals/ProposalList";
import ProposalDetail from "./pages/Proposals/ProposalDetail";
import ProposalNew from "./pages/Proposals/ProposalNew";
import PortalProposals from "./pages/Proposals/PortalProposals";
import PortalProposalDetail from "./pages/Proposals/PortalProposalDetail";

// Shared pages (vendor + partner)
import MyProfile from "./pages/Profile/MyProfile";

// ─── Route guards ──────────────────────────────────────────────────────────────

function ProtectedRoute({ children, allowedTypes }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <svg
            className="animate-spin h-10 w-10 text-brand-500 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (allowedTypes && !allowedTypes.includes(user.userType)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
}

function MasterOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.userType !== "admin" || user.role !== "MASTER") {
    return <Navigate to="/dashboard" replace />;
  }
  return <Layout>{children}</Layout>;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

// ─── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <Routes>
      {/* ── Public ──────────────────────────────────────────────────────── */}
      <Route
        path="/login"
        element={
          <GuestOnly>
            <Login />
          </GuestOnly>
        }
      />
      <Route
        path="/register"
        element={
          <GuestOnly>
            <Register />
          </GuestOnly>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <GuestOnly>
            <ForgotPassword />
          </GuestOnly>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <GuestOnly>
            <ResetPassword />
          </GuestOnly>
        }
      />

      {/* ── Dashboard — all authenticated users ─────────────────────────── */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* ── Profile — vendor and partner ────────────────────────────────── */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute allowedTypes={["vendor", "partner"]}>
            <MyProfile />
          </ProtectedRoute>
        }
      />

      {/* ── Entities — admin only ───────────────────────────────────────── */}
      <Route
        path="/entities"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <EntityList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/entities/:id"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <EntityDetail />
          </ProtectedRoute>
        }
      />

      {/* ── Groups — admin only ─────────────────────────────────────────── */}
      <Route
        path="/groups"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <GroupManagement />
          </ProtectedRoute>
        }
      />

      {/* ── Vendors ─────────────────────────────────────────────────────── */}
      <Route
        path="/vendors"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <VendorList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendors/:id"
        element={
          <ProtectedRoute allowedTypes={["admin", "vendor"]}>
            <VendorDetail />
          </ProtectedRoute>
        }
      />

      {/* ── Partners ────────────────────────────────────────────────────── */}
      <Route
        path="/partners"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <PartnerList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partners/new"
        element={
          <MasterOnly>
            <PartnerNew />
          </MasterOnly>
        }
      />
      <Route
        path="/partners/:id"
        element={
          <ProtectedRoute allowedTypes={["admin", "partner"]}>
            <PartnerDetail />
          </ProtectedRoute>
        }
      />

      {/* ── Agreements — all authenticated users ────────────────────────── */}
      <Route
        path="/agreements"
        element={
          <ProtectedRoute>
            <AgreementList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agreements/new"
        element={
          <ProtectedRoute allowedTypes={["vendor", "partner", "admin"]}>
            <AgreementNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agreements/:id"
        element={
          <ProtectedRoute>
            <AgreementDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agreements/:id/addendum/new"
        element={
          <ProtectedRoute allowedTypes={["vendor", "partner", "admin"]}>
            <AddendumNew />
          </ProtectedRoute>
        }
      />

      {/* ── Permits — admin only ────────────────────────────────────────────── */}
      <Route
        path="/permits"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <PermitList />
          </ProtectedRoute>
        }
      />

      {/* ── Templates — admin (full) + vendor/partner (read-only) ─────────── */}
      <Route
        path="/templates"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <TemplateList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/:id"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <TemplateDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/templates"
        element={
          <ProtectedRoute allowedTypes={["vendor", "partner"]}>
            <PortalTemplates />
          </ProtectedRoute>
        }
      />

      {/* ── Proposals — admin (review) + vendor (submit/track) ──────────── */}
      <Route
        path="/proposals"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <ProposalList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/proposals/:id"
        element={
          <ProtectedRoute allowedTypes={["admin"]}>
            <ProposalDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/proposals"
        element={
          <ProtectedRoute allowedTypes={["vendor"]}>
            <PortalProposals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/proposals/new"
        element={
          <ProtectedRoute allowedTypes={["vendor"]}>
            <ProposalNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portal/proposals/:id"
        element={
          <ProtectedRoute allowedTypes={["vendor"]}>
            <PortalProposalDetail />
          </ProtectedRoute>
        }
      />

      {/* ── Settings — master only ───────────────────────────────────────── */}
      <Route
        path="/settings/admins"
        element={
          <MasterOnly>
            <AdminSettings />
          </MasterOnly>
        }
      />

      {/* ── Fallbacks ───────────────────────────────────────────────────── */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
